/**
 * X98 1K / 8815 机芯：静态基准数据（m80-1k / m71-1k 的 `M80_DEFAULT_POS_BY_KEYCODE`、`M80_KEYS_BEIYING` 同构）
 * - 8815 无 getDefaultKeyLayout，默认键位以本文件为**唯一**权威（用户提供的 layer0 全表 JSON，勿混用 916 协议表）。
 * - `BEIYING_8815_LAYER0_KEYBOARD_KEYS`：getKeyboardKeys 全项（pos、value、effect_pos、light_pos、name、layer=0，…）；
 * - `X98_1K_8815_FACTORY_POS_TO_KEYCODE` / `X98_1K_8815_FACTORY_KEY_ENTRIES`：出厂 `pos`→ 固件 `value`（setKeyboardKeys 的 `value`）；
 * - `X98_1K_DEFAULT_POS_BY_KEYCODE`：固件 value（与 KEYCODE_MAP 中 keycode 一致，如 131072=L Shift）→ `pos`，首次非 0；
 * 键槽/矩阵在 `keyCodeMapperBeiying`：`X98_1K_KEYS_BEIYING`（`m80-1k` 的 M80KeySlot 同形）。
 */
import x98Layer0Json from "./x98_1k_getKeyboardKeys_layer0.json";

export interface Beiying8815GetKeyboardKeyItem {
  value: number;
  pos: number;
  effect_pos: number;
  light_pos: number;
  name: string;
  type: string;
  macroIndex: string;
  layer: number;
}

/** 8815 机芯 layer0 工厂表（与 web-hid 用的 `KEYCODE_MAP_BEIYING_RAW` 键帽表无关，勿混用） */
export const BEIYING_8815_LAYER0_KEYBOARD_KEYS: Beiying8815GetKeyboardKeyItem[] =
  x98Layer0Json as Beiying8815GetKeyboardKeyItem[];

function buildX98815FactoryPosToKeycode(
  raw: Beiying8815GetKeyboardKeyItem[]
): Readonly<Record<number, number>> {
  const o: Record<number, number> = {};
  for (const row of raw) {
    const pos = Number(row.pos);
    if (!Number.isFinite(pos)) continue;
    o[pos] = Number(row.value);
  }
  return o;
}

/** 由 8815 layer0 工厂表生成；恢复默认 = 用此表写回 0/1/2 层（当前仅有 L0 基准样例，Fn 层同表，与 m80 静态表用法一致，后续可再拆层） */
export const X98_1K_8815_FACTORY_POS_TO_KEYCODE: Readonly<
  Record<number, number>
> = buildX98815FactoryPosToKeycode(BEIYING_8815_LAYER0_KEYBOARD_KEYS);

export const X98_1K_8815_FACTORY_KEY_ENTRIES: ReadonlyArray<{
  pos: number;
  value: number;
}> = (() => {
  const map = X98_1K_8815_FACTORY_POS_TO_KEYCODE as Readonly<
    Record<string, number>
  >;
  const out: { pos: number; value: number }[] = [];
  for (const k of Object.keys(map).sort((a, b) => Number(a) - Number(b))) {
    const pos = Number(k);
    if (!Number.isFinite(pos)) continue;
    out.push({ pos, value: (map as Record<string, number>)[k] });
  }
  return out;
})();

function buildX98DefaultPosByKeycode(
  raw: Beiying8815GetKeyboardKeyItem[]
): Readonly<Record<number, number>> {
  const o: Record<number, number> = {};
  for (const row of raw) {
    const v = Number(row.value);
    if (!Number.isFinite(v) || v === 0) continue;
    if (o[v] !== undefined) continue;
    o[v] = row.pos;
  }
  return o;
}

/**
 * 以固件回传的 value（keycode）定位改键/矩阵用 `pos`（本表由 RAW 工厂表生成）。
 * 若同一 `value` 在 RAW 中多次出现（不应对非 0 出现），只保留 first。
 */
export const X98_1K_DEFAULT_POS_BY_KEYCODE: Readonly<Record<number, number>> =
  buildX98DefaultPosByKeycode(BEIYING_8815_LAYER0_KEYBOARD_KEYS);

/**
 * 与 m80-1k `M80KeySlot` 同形；`keycode` 为**矩阵/布局**上的码（与 `KEYBOARD_LAYOUTS.X98_1K` 格内一致，如 225=左移 xs），
 * 非固件 131072 形态；`firmwareValue` 为 8815 getKeyboardKeys 的 `value` 字段，便于对照 RAW。
 */
export type X98_1KKeySlot = {
  keycode: number;
  pos: number;
  row: number;
  col: number;
  /** 同 RAW 的 value，如 L_Shift=131072 */
  firmwareValue: number;
};
